import { useRef, useState } from "react";
import { ChevronLeft, Check, Camera, MessageSquarePlus, ThumbsUp, RotateCcw, Ban, Eye, Send, Loader2, UserPlus, X, Pin, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STATUS_WORDS, TONE_CLS, clock, pretty } from "./util";

const Line = ({ children }) => <div className="text-sm text-muted-foreground">{children}</div>;

// Two plain choices with their consequence written underneath, the house style for anything that
// changes who gets told what.
const Choice = ({ label, value, onPick, options, testid }) => (
  <div>
    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1.5 space-y-1.5">
      {options.map(([val, title, hint]) => (
        <label key={String(val)} className="flex cursor-pointer items-start gap-2 text-sm">
          <input type="radio" checked={value === val} onChange={() => onPick(val)}
            data-testid={`visit-${testid}-${val ? "on" : "off"}`} className="mt-1 accent-redline-500" />
          <span><span className="font-semibold">{title}</span>
            <span className="block text-xs text-muted-foreground">{hint}</span></span>
        </label>
      ))}
    </div>
  </div>
);

// Work that didn't get done on the day. The owner's rule: nothing is quietly forgiven — the day it
// was carried FROM stays unfinished until the leftovers are done, then reads "finished late".
const Carry = ({ visit }) => (
  <>
    {visit.carried && (
      <div className="mt-3 rounded-r-xl border-l-[3px] border-amber-400 bg-amber-50/70 px-3.5 py-2.5" data-testid="visit-carried-out">
        <div className="text-xs font-bold uppercase tracking-wider text-amber-800">
          <ArrowRightCircle className="mr-1 inline h-3 w-3" />
          {visit.carried.count} task(s) moved to {pretty(visit.carried.to_date)}
        </div>
        <p className="mt-0.5 text-sm text-ink">
          “{visit.carried.reason}” — {visit.carried.by || "the crew"}, {visit.carried.at}
        </p>
      </div>
    )}
    {visit.carried_in && (
      <div className="mt-3 rounded-r-xl border-l-[3px] border-indigo-400 bg-indigo-50/60 px-3.5 py-2.5" data-testid="visit-carried-in">
        <div className="text-xs font-bold uppercase tracking-wider text-indigo-800">
          {visit.carried_in.count} task(s) carried over from {pretty(visit.carried_in.from_date)}
        </div>
        <p className="mt-0.5 text-sm text-ink">“{visit.carried_in.reason}”</p>
      </div>
    )}
    {visit.closed_late && (
      <div className="mt-3 text-sm font-semibold text-amber-700" data-testid="visit-closed-late">
        Finished late — the carried work was done on {pretty(visit.closed_late.on_date)}
      </div>
    )}
  </>
);

// Pinned by the office, sitting on top of the day for the crew to see before they start.
const Pinned = ({ notes, onRemove, busy }) => !notes?.length ? null : (
  <div className="mt-3 space-y-2" data-testid="visit-pinned">
    {notes.map((n) => (
      <div key={n.id} data-testid={`visit-pinned-${n.id}`}
        className="rounded-r-xl border-l-[3px] border-amber-400 bg-amber-50/70 px-3.5 py-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-800">
            <Pin className="mr-1 inline h-3 w-3" />
            Note for the crew{n.always ? " · on every visit" : ""}
          </div>
          {onRemove && (
            <button onClick={() => onRemove(n.id)} disabled={busy} data-testid={`visit-unpin-${n.id}`}
              className="shrink-0 rounded-full p-1 text-amber-700/60 hover:bg-amber-100 hover:text-rose-600 disabled:opacity-50">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {n.text && <p className="mt-0.5 text-sm text-ink">{n.text}</p>}
        {(n.from || n.until) && (
          <div className="mt-1 text-xs font-semibold text-amber-800" data-testid={`visit-pinned-season-${n.id}`}>
            {n.from ? pretty(n.from) : "from today"} – {n.until ? pretty(n.until) : "no end date"}
          </div>
        )}
        <div className="mt-1 text-xs text-muted-foreground" data-testid={`visit-pinned-ack-${n.id}`}>
          {n.ack ? <span className="font-semibold text-emerald-700">Read by {n.ack.name || "the crew"} · {n.ack.at}</span>
            : "Not read yet"}
        </div>
        {!!n.photos?.length && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {n.photos.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`visit-pinned-photo-${n.id}-${i}`}>
                <img src={u} alt="" className="h-16 w-16 rounded-lg border border-amber-200 object-cover" />
              </a>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

const NOTICE_WORDS = {
  today: "the list", nudge: "a nudge", missed: "a missed notice", question: "your question",
  question_photo: "your feedback", link: "their link",
};
const DELIVERY = {
  delivered: ["delivered", "text-emerald-700"], sent: ["sent", "text-muted-foreground"],
  queued: ["queued", "text-muted-foreground"], emailed: ["emailed", "text-muted-foreground"],
  failed: ["NOT delivered — the carrier rejected it", "text-rose-700 font-semibold"],
  undelivered: ["NOT delivered — it never reached their phone", "text-rose-700 font-semibold"],
  stopped: ["blocked — they replied STOP", "text-rose-700 font-semibold"],
  unreachable: ["nowhere to send it — no mobile, no email", "text-rose-700 font-semibold"],
};

// "Was the assigned person even told, and did they look?" — the manager could not answer this.
const Notified = ({ n, over }) => {
  if (!n) return null;
  const bad = n.problem === "failed";
  return (
    <div data-testid="visit-notified"
      className={`mt-3 rounded-r-xl border-l-[3px] px-3.5 py-2 text-sm ${bad ? "border-rose-300 bg-rose-50" : "border-sand-300 bg-sand-50"}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Were they told?</div>
      {!n.notices.length && (
        <p className="mt-1 text-muted-foreground" data-testid="visit-notified-none">
          Nothing has gone out on this visit yet — the reminder goes at the time this checklist is set to.
        </p>
      )}
      {n.notices.map((x) => {
        const [word, cls] = DELIVERY[x.status] || [x.status, "text-muted-foreground"];
        return (
          <p key={x.id} className="mt-1" data-testid={`visit-notice-${x.id}`}>
            <span className="font-semibold text-ink">{x.to}</span> was sent {NOTICE_WORDS[x.kind] || x.kind} by{" "}
            {x.channel === "email" ? "email" : x.channel === "both" ? "text and email" : "text"} · {x.at} ·{" "}
            <span className={cls}>{word}</span>{x.error ? ` (${x.error})` : ""}
          </p>
        );
      })}
      <p className={`mt-1 ${n.opened_at ? "text-emerald-700" : "text-amber-700"}`} data-testid="visit-notified-opened">
        {n.opened_at
          ? `Opened the list ${n.opened_at}${n.opened_by ? ` — ${n.opened_by}` : ""}`
          : n.notices.length && over ? "Never opened the list" : "Not opened yet"}
      </p>
    </div>
  );
};

export function VisitRecord({ tenantId, visit, onBack, onChange, readOnly, today }) {
  const [ask, setAsk] = useState(null);           // { task_id } | { redo: true }
  const [text, setText] = useState("");
  const [shots, setShots] = useState([]);         // [{ path, url }] already uploaded
  const [sendBack, setSendBack] = useState(false);
  const [always, setAlways] = useState(false);
  const [tellNow, setTellNow] = useState(false);
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileRef = useRef(null);
  const st = STATUS_WORDS[visit.status] || STATUS_WORDS.scheduled;
  // Nothing has happened yet on a future visit — the only sensible action is to excuse it.
  const ahead = !!today && visit.date > today && visit.status === "scheduled";
  const closed = ["approved", "excused"].includes(visit.status) || ahead;
  // A finished visit still takes feedback: "it says completed but I've no way to tell them it
  // wasn't done properly" is the whole point of this panel.
  const canTalk = !readOnly && !ahead;
  // A note is a heads-up, so it belongs on a day that hasn't been filed yet. Once a visit is done,
  // anything you want to say is feedback.
  const canNote = !readOnly && ["scheduled", "in_progress"].includes(visit.status);
  const sections = [...new Set(visit.tasks.map((t) => t.section))];
  // Pinned notes have their own block at the top, so they don't repeat down in the conversation.
  const said = (visit.thread || []).filter((e) => e.kind !== "note");
  const labelOf = (id) => visit.tasks.find((t) => t.id === id)?.label;

  const openAsk = (shape) => {
    setAsk(shape); setText(""); setShots([]); setSendBack(!!shape.redo);
    setAlways(false); setTellNow(false); setFrom(""); setUntil("");
  };

  const unpin = async (nid) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/tenants/${tenantId}/checklists/visits/${visit.id}/note/${nid}`);
      if (data.visit) onChange(data.visit);
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't come off"); } finally { setBusy(false); }
  };

  const act = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/checklists/visits/${visit.id}/${path}`, body || {});
      if (data.visit) onChange(data.visit);
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't go through"); return null; } finally { setBusy(false); }
  };

  const addPhotos = async (files) => {
    const picked = [...files].slice(0, 6 - shots.length);
    for (let i = 0; i < picked.length; i++) {
      setUploading(`${i + 1}/${picked.length}`);
      const fd = new FormData();
      fd.append("file", picked[i]);
      try {
        const { data } = await api.post(`/tenants/${tenantId}/checklists/visits/${visit.id}/photo`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        setShots((s) => [...s, { path: data.path, url: data.url }]);
      } catch (e) { toast.error(e?.response?.data?.detail || "That photo didn't upload"); }
    }
    setUploading("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async () => {
    if (!text.trim() && !shots.length) return;
    if (ask?.note) {
      const r = await act("note", { text: text.trim(), task_id: ask?.task_id, always, tell_now: tellNow,
                                    from: always ? from : "", until: always ? until : "",
                                    photos: shots.map((s) => s.path) });
      if (r?.ok) {
        toast.success(always ? (from || until ? "Pinned for those dates" : "Pinned to every visit from now on")
          : tellNow ? "Pinned to this visit — the crew has been told now"
            : "Pinned to this visit — it goes out with their text that morning");
        setText(""); setShots([]); setAsk(null);
      }
      return;
    }
    const r = await act("comment", {
      text: text.trim(), task_id: ask?.task_id, redo: !!ask?.redo,
      reopen: sendBack, photos: shots.map((s) => s.path),
    });
    if (r?.ok) {
      toast.success(closed && !sendBack ? "Noted on the record — the crew has been told"
        : "Sent to the crew — it's waiting on their link");
      setText(""); setShots([]); setAsk(null);
    }
  };

  // The office's half of a crew addition: keep it for every future visit, or take it back off.
  const keepTask = async (task, keep) => {
    const r = await act("keep-task", { task_id: task.id, keep });
    if (r?.ok) toast.success(keep ? "Kept — it's on every visit from now on" : "Taken off the checklist");
  };

  return (
    <div data-testid="visit-record">
      <button onClick={onBack} data-testid="visit-record-back"
        className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-ink">
        <ChevronLeft className="h-4 w-4" /> All visits
      </button>

      <div className="rounded-2xl border border-sand-200 bg-white p-6">
        <h2 className="font-heading text-xl font-bold tracking-tight">{visit.name}</h2>
        <Line>
          {pretty(visit.date)} · <span className={`font-semibold ${TONE_CLS[st.tone]}`} data-testid="visit-record-status">{st.label}</span>
        </Line>
        <Line>
          {visit.tech_name ? `${visit.tech_name} on site` : "Nobody checked in"}
          {visit.started_at
            ? ` · ${visit.started_at}${visit.submitted_at && visit.submitted_at !== "window closed" ? `–${visit.submitted_at}` : ""}`
            : ` · due by ${clock(visit.window?.end)}`}
          {" · "}{visit.counts.done} of {visit.counts.total} ticked
          {visit.counts.photos ? ` · ${visit.counts.photos} photo${visit.counts.photos > 1 ? "s" : ""}` : ""}
        </Line>
        {visit.auto_filed && (
          <p className="mt-2 rounded-r-xl border-l-[3px] border-amber-300 bg-amber-50 px-3.5 py-2 text-sm text-amber-900"
            data-testid="visit-auto-filed">
            Left open — nobody filed it before the window closed, so it was filed as it stood. Nothing was changed.
          </p>
        )}
        {visit.late && (
          <p className="mt-2 rounded-r-xl border-l-[3px] border-sky-300 bg-sky-50 px-3.5 py-2 text-sm text-sky-900"
            data-testid="visit-late">
            Filed late — the crew came back to it after the window had closed. The work is real; the timing wasn't.
          </p>
        )}
        {visit.status === "missed" && visit.miss_streak > 1 && (
          <p className="mt-2 rounded-r-xl border-l-[3px] border-rose-300 bg-rose-50 px-3.5 py-2 text-sm text-rose-900"
            data-testid="visit-miss-streak">
            That's {visit.miss_streak} scheduled visits in a row with nothing ticked. This is a vendor conversation now.
          </p>
        )}

        <Notified n={visit.notified} over={["missed", "excused", "approved"].includes(visit.status)} />
        <Pinned notes={visit.pinned} onRemove={readOnly ? null : unpin} busy={busy} />
        <Carry visit={visit} />

        {!readOnly && (
          <div className="mt-5 flex flex-wrap gap-2">
            {!closed && visit.counts.done > 0 && <button onClick={() => act("approve")} disabled={busy} data-testid="visit-approve"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              <ThumbsUp className="h-3.5 w-3.5" /> Approve
            </button>}
            {canNote && <button onClick={() => openAsk({ note: true })} data-testid="visit-note"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ${
                ahead ? "bg-ink text-white hover:bg-ink/90" : "border border-sand-300 bg-white hover:border-sand-400"}`}>
              <Pin className="h-3.5 w-3.5" /> Leave a note
            </button>}
            {canTalk && <button onClick={() => openAsk({})} data-testid="visit-ask"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ${
                closed ? "bg-ink text-white hover:bg-ink/90" : "border border-sand-300 bg-white hover:border-sand-400"}`}>
              <MessageSquarePlus className="h-3.5 w-3.5" /> {closed ? "Leave feedback" : "Ask a question"}
            </button>}
            {canTalk && visit.counts.done > 0 && <button onClick={() => openAsk({ redo: true })} data-testid="visit-redo"
              className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-4 py-2 text-sm font-semibold hover:border-sand-400">
              <RotateCcw className="h-3.5 w-3.5" /> Request re-do
            </button>}
            {!["approved", "excused"].includes(visit.status) && <button onClick={() => act("excuse")} disabled={busy} data-testid="visit-excuse"
              className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-4 py-2 text-sm font-semibold hover:border-sand-400">
              <Ban className="h-3.5 w-3.5" /> Excuse
            </button>}
            <button onClick={() => act("share", { shared: !visit.board_shared })} disabled={busy} data-testid="visit-share"
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold ${
                visit.board_shared ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-sand-300 bg-white hover:border-sand-400"}`}>
              <Eye className="h-3.5 w-3.5" /> {visit.board_shared ? "Shared with the board" : "Share with the board"}
            </button>
          </div>
        )}
      </div>

      {ask && (
        <div className="mt-3 rounded-2xl border border-sand-200 bg-white p-5" data-testid="visit-ask-box">
          <div className="text-sm font-semibold">
            {ask.note ? (ask.task_id ? `Note on “${labelOf(ask.task_id)}”` : "Leave a note for the crew")
              : ask.redo ? "Request a re-do" : ask.task_id ? `Feedback on “${labelOf(ask.task_id)}”`
                : closed ? "Feedback on this visit" : "Ask the vendor"}
          </div>
          <textarea rows={3} autoFocus value={text} onChange={(e) => setText(e.target.value)} data-testid="visit-ask-text"
            placeholder={ask.note ? "e.g. The garage gate will be locked — the key is at the front desk"
              : ask.redo ? "What needs doing again, and by when?"
                : closed ? "e.g. The lobby glass was missed — see the photo"
                  : "e.g. Why was the trash room skipped again?"}
            className="mt-2 w-full resize-none rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {shots.map((s) => (
              <span key={s.path} className="relative" data-testid={`visit-ask-photo-${shots.indexOf(s)}`}>
                <img src={s.url} alt="" className="h-16 w-16 rounded-xl border border-sand-200 object-cover" />
                <button onClick={() => setShots((v) => v.filter((x) => x.path !== s.path))}
                  data-testid={`visit-ask-photo-remove-${shots.indexOf(s)}`}
                  className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full border border-sand-300 bg-white text-ink/60 hover:text-rose-600">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {shots.length < 6 && (
              <button onClick={() => fileRef.current?.click()} disabled={!!uploading} data-testid="visit-ask-photo-add"
                className="grid h-16 w-16 place-items-center rounded-xl border border-dashed border-sand-300 text-sand-500 hover:border-redline-400 hover:text-redline-500 disabled:opacity-50">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple hidden data-testid="visit-ask-photo-input"
              onChange={(e) => e.target.files?.length && addPhotos(e.target.files)} />
            <span className="text-xs text-muted-foreground">
              {uploading ? `Uploading ${uploading}…` : shots.length ? "They'll see these on their link" : "Add a photo of what you mean"}
            </span>
          </div>

          {ask.note && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="visit-note-choices">
              <Choice label="How long it stays" value={always} onPick={setAlways} testid="note-scope"
                options={[[false, "Just this visit", `Only ${pretty(visit.date)}.`],
                          [true, "Every visit from now on", "Pinned to this list until you take it off."]]} />
              <Choice label="When they hear" value={tellNow} onPick={setTellNow} testid="note-when"
                options={[[false, "With their text that morning", "Their usual message says a note is waiting."],
                          [true, "Tell them now", "A text goes out straight away."]]} />
              {always && (
                <div className="sm:col-span-2" data-testid="visit-note-season">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Only between these dates <span className="font-semibold normal-case tracking-normal">(optional — leaf season, holiday decorations)</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="visit-note-from"
                      className="rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                    <span className="text-muted-foreground">to</span>
                    <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="visit-note-until"
                      className="rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                    {(from || until) && (
                      <button onClick={() => { setFrom(""); setUntil(""); }} data-testid="visit-note-season-clear"
                        className="text-xs font-semibold text-muted-foreground underline underline-offset-2">clear</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {closed && !ask.redo && !ask.note && (
            <div className="mt-3 space-y-1.5" data-testid="visit-ask-mode">
              {[[false, "Just note it on the record", "The visit stays complete. They still get the text."],
                [true, "Send it back for a re-do", "Reopens the visit as needing attention."]].map(([val, label, hint]) => (
                <label key={String(val)} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input type="radio" checked={sendBack === val} onChange={() => setSendBack(val)}
                    data-testid={`visit-ask-mode-${val ? "redo" : "note"}`} className="mt-1 accent-redline-500" />
                  <span><span className="font-semibold">{label}</span>
                    <span className="block text-xs text-muted-foreground">{hint}</span></span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {ask.note ? "A note never marks the day as a problem — it just waits on their phone."
                : "They're told straight away and it waits on their link. Their reply lands back here."}
            </span>
            <div className="flex gap-2">
              <button onClick={() => setAsk(null)} className="rounded-full border border-sand-300 px-4 py-2 text-sm font-semibold">Cancel</button>
              <button onClick={send} disabled={busy || !!uploading || (!text.trim() && !shots.length)} data-testid="visit-ask-send"
                className="inline-flex items-center gap-1.5 rounded-full bg-redline-500 px-5 py-2 text-sm font-semibold text-white hover:bg-redline-600 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 rounded-2xl border border-sand-200 bg-white p-6" data-testid="visit-tasks">
        {sections.map((sec) => (
          <div key={sec} className="mb-5 last:mb-0">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{sec}</div>
            <div className="mt-2 divide-y divide-sand-100">
              {visit.tasks.filter((t) => t.section === sec).map((task) => (
                <div key={task.id} className="flex items-start gap-3 py-2.5" data-testid={`record-task-${task.id}`}>
                  <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md border-2 ${
                    task.done ? "border-emerald-600 bg-emerald-600" : "border-sand-300 bg-white"}`}>
                    {task.done && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-semibold ${task.done ? "" : "text-muted-foreground"}`}>{task.label}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-muted-foreground">
                      {task.carried_from && (
                        <span className="font-semibold text-indigo-700" data-testid={`record-carried-${task.id}`}>
                          carried over from {pretty(task.carried_from.date, { weekday: "short", year: undefined })}
                        </span>
                      )}
                      {task.done ? <span>{task.at}</span> : task.skipped
                        ? <span className="text-amber-700">Couldn't do it: {task.reason}</span>
                        : <span className="text-amber-700">not ticked — no reason given</span>}
                      {task.photo_required && !task.photos.length && <span className="inline-flex items-center gap-1 text-amber-700"><Camera className="h-3 w-3" /> photo required</span>}
                      {task.added_by && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 font-semibold text-sky-800"
                          data-testid={`record-added-${task.id}`}>
                          <UserPlus className="h-3 w-3" /> added by {task.added_by}
                          {task.added_at ? ` · ${task.added_at}` : ""}{task.kept ? " · kept on the list" : " · today only"}
                        </span>
                      )}
                    </div>
                    {(task.steps || []).length > 0 && (
                      <div className="mt-1.5 space-y-1" data-testid={`record-steps-${task.id}`}>
                        {task.steps.map((s) => (
                          <div key={s.id} className="flex items-center gap-2">
                            <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 ${
                              s.done ? "border-emerald-600 bg-emerald-600" : "border-sand-300 bg-white"}`}>
                              {s.done && <Check className="h-2.5 w-2.5 text-white" strokeWidth={4} />}
                            </span>
                            <span className={`text-xs ${s.done ? "" : "text-muted-foreground"}`}>{s.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {task.note && (
                      <p className="mt-1.5 rounded-lg bg-sand-50 px-3 py-2 text-xs leading-relaxed text-ink-700"
                        data-testid={`record-note-${task.id}`}>{task.note}</p>
                    )}
                    {task.photos.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {task.photos.map((u, i) => (
                          <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`record-photo-${task.id}-${i}`}>
                            <img src={u} alt="" className="h-16 w-16 rounded-lg border border-sand-200 object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    {task.added_by && !readOnly && (
                      <button onClick={() => keepTask(task, !task.kept)} data-testid={`record-keep-${task.id}`}
                        disabled={busy}
                        className="mt-2 text-xs font-semibold text-redline-600 underline underline-offset-2 hover:text-redline-700 disabled:opacity-40">
                        {task.kept ? "Undo — take it off the checklist" : "Keep this on the checklist"}
                      </button>
                    )}
                  </div>
                  {canTalk && (
                    <button onClick={() => openAsk({ task_id: task.id })} data-testid={`record-ask-${task.id}`}
                      title="Feedback on this task"
                      className="shrink-0 rounded-full p-1.5 text-sand-400 hover:bg-sand-100 hover:text-redline-500">
                      <MessageSquarePlus className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {visit.note && (
          <div className="mt-4 rounded-r-xl border-l-[3px] border-sand-300 bg-sand-50 px-4 py-3">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Their note</div>
            <p className="mt-1 text-sm leading-relaxed">{visit.note}</p>
          </div>
        )}
      </div>

      <div className="mt-3 rounded-2xl border border-sand-200 bg-white p-6" data-testid="visit-conversation">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Conversation</div>
        {!said.length && (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="visit-conversation-empty">
            Nothing said yet. Anything you write reaches the crew straight away and waits on their link; their reply comes back here.
          </p>
        )}
        <div className="mt-3 space-y-3">
          {said.map((e) => (
            <div key={e.id} data-testid={`thread-${e.id}`} className="text-sm">
              <div className="text-xs text-muted-foreground">
                <span className="font-semibold text-ink">{e.side === "manager" ? e.name : e.name || "Vendor"}</span>
                {e.task_id && <> · on “{labelOf(e.task_id)}”</>} · {e.at}
                {e.kind === "issue" && <span className="ml-1.5 font-semibold text-amber-700">flagged</span>}
                {e.kind === "redo" && <span className="ml-1.5 font-semibold text-rose-700">re-do</span>}
              </div>
              {e.text && <p className={`mt-0.5 ${e.kind === "system" ? "text-muted-foreground" : ""}`}>{e.text}</p>}
              {(e.photo || (e.photos || []).length > 0) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[...(e.photo ? [e.photo] : []), ...(e.photos || [])].map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`thread-photo-${e.id}-${i}`}>
                      <img src={u} alt="" className="h-16 w-16 rounded-lg border border-sand-200 object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
